import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import { State } from '@completion/reducers';
import { CpService } from '@completion/services';
import { UserPreferenceService } from '../../services/user-preference.service';
import {
  AddCpCheckRecord,
  CheckSheetActionTypes,
  DeleteCpCheckRecord,
  LoadCurrentCpCsa,
  LoadCurrentCsaSuccess,
  UpdateCpCsa,
  UpdateCpCsItemStatus,
  UpdateCpRecordValue,
  UpdateCsItemStatusFailure,
  UpdateCsItemStatusSuccess,
  RecalculateCsaStatuses,
  FetchCpSignaturesFromHistory,
  UploadCpCsaFile,
  DeleteCpCsaAttachment,
  FetchCpItemStatusesFromHistory,
  CpMoveCSA,
} from '@completion/actions';
import {
  CpActionTypes,
  CpAddCheckedTags,
  CpDeleteCSA,
  InsertAndSignCpCsaSigntureEntry,
  LoadCurrentCpSuccess,
  SignCpCsaSigntureEntry,
} from '@completion/actions';
import { CpScopeOfWork, PackageCommonActionTypes, CpScopeOfWorkSuccess } from '@completion/actions';
import { getCurrentCsaId } from '../selectors/check-sheet.selector';
import { getCurrentCpId } from '../selectors/cp.selector';
import { getCurrentProject } from '../selectors/project.selector';
import { Project } from '@completion/models';
import { UserPreference } from '@completion/enums';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class CpCheckSheetEffect {
  constructor(
    private actions$: Actions,
    private cpService: CpService,
    private readonly userPreferenceService: UserPreferenceService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadCpScopeOfWork$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpScopeOfWork>(PackageCommonActionTypes.CpScopeOfWork),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project> as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number> as Observable<number>,
      ),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'getCheckedTags',
        serviceArgs: ([_, currentProject, cpId]) => [
          currentProject.id,
          cpId,
          this.userPreferenceService.isEnabled(UserPreference.USE_FLAT_TAG_TABLE_VIEW),
        ],
        onSuccess: (response) => new CpScopeOfWorkSuccess(response as any),
      }),
    ),
  );

  addCheckedTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpAddCheckedTags>(CpActionTypes.CpAddCheckedTags),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'addCheckedTags',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.checkSheetAssignment],
        onSuccess: () => null,
      }),
    ),
  );

  deleteCheckSheetAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpDeleteCSA>(CpActionTypes.CpDeleteCSA),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'deleteCpCheckSheetAssignment',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.ids],
        onSuccess: () => null,
      }),
    ),
  );

  moveTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CpMoveCSA>(CpActionTypes.CpMoveCSA),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'moveTags',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.csas],
        onSuccess: () => null,
      }),
    ),
  );

  loadCpCsa$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadCurrentCpCsa>(CheckSheetActionTypes.LoadCurrentCpCsa),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number> as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'getCheckSheetAssignment',
        serviceArgs: ([_, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  updateCpCsItemStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCpCsItemStatus>(CheckSheetActionTypes.UpdateCpCsItemStatus),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number> as Observable<number>,
      ),
      filter(([_, project, cpId, csaId]) => !!(project && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'updateCpCheckListItem',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.item],
        onSuccess: (csa: any, sourceArgs) => {
          const action = Array.isArray(sourceArgs?.[0]) ? sourceArgs[0][0] : sourceArgs?.[0];
          const itemNumber: number = action?.item?.number;
          const updatedItem = (csa?.csItems ?? []).find((i: any) => i.number === itemNumber) ?? action?.item;
          return new UpdateCsItemStatusSuccess(itemNumber, updatedItem);
        },
        onError: (err, sourceArgs) => {
          const action = Array.isArray(sourceArgs?.[0]) ? sourceArgs[0][0] : sourceArgs?.[0];
          const msg = err?.error?.message || err?.error?.error || err?.message || 'Unknown error';
          return new UpdateCsItemStatusFailure(action?.item?.number, msg);
        },
      }),
    ),
  );

  updateCpCsa$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCpCsa>(CheckSheetActionTypes.UpdateCpCsa),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'updateCpCheckSheetAssignment',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.csa, action.doSignOrVerify],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  updateCpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateCpRecordValue>(CheckSheetActionTypes.UpdateCpRecordValue),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number> as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'updateCpRecordValue',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.csItemNumber, action.recordValue],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  addCpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddCpCheckRecord>(CheckSheetActionTypes.AddCpCheckRecord),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'addCpRecordValue',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.csItem.number, action.recordValue],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  deleteCpRecordValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteCpCheckRecord>(CheckSheetActionTypes.DeleteCpCheckRecord),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'deleteCpRecordValue',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.csItemNumber, action.recordValue],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  updateCpCsaStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RecalculateCsaStatuses>(CheckSheetActionTypes.RecalculateCsaStatuses),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'updateCpCsaStatuses',
        serviceArgs: ([_, currentProject, cpId]) => [currentProject.id, cpId],
        onSuccess: (response) => new LoadCurrentCpSuccess(response as any),
      }),
    ),
  );

  fetchCpSignaturesFromHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchCpSignaturesFromHistory>(CheckSheetActionTypes.FetchCpSignaturesFromHistory),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'fetchCpSignaturesFromHistory',
        serviceArgs: ([_, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  fetchCpItemStatusesFromHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchCpItemStatusesFromHistory>(CheckSheetActionTypes.FetchCpItemstatusesFromHistory),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'fetchCpItemStatusesFromHistory',
        serviceArgs: ([_, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  signCpCsaSignatureEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignCpCsaSigntureEntry>(CpActionTypes.SignCpCsaSigntureEntry),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_action, currentProject, cpId, _csaId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'signCpCsaSigntureEntry',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.entry.id],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  insertAndSignCpCsaSignatureEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType<InsertAndSignCpCsaSigntureEntry>(CpActionTypes.InsertAndSignCpCsaSigntureEntry),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_action, currentProject, cpId, _csaId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'insertAndSignCpCsaSigntureEntry',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, true, [action.entry]],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  uploadCpCsaFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UploadCpCsaFile>(CheckSheetActionTypes.UploadCpCsaFile),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_action, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'uploadCpCsaFile',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.file],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );

  deleteCpCsaAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteCpCsaAttachment>(CheckSheetActionTypes.DeleteCpCsaAttachment),
      withLatestFrom(
        this.store.select(getCurrentProject) as Observable<Project>,
        this.store.select(getCurrentCpId) as Observable<number>,
        this.store.select(getCurrentCsaId) as Observable<number>,
      ),
      filter(([_action, currentProject, cpId, csaId]) => !!(currentProject && cpId && csaId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.cpService,
        method: 'deleteCpCsaAttachment',
        serviceArgs: ([action, currentProject, cpId, csaId]) => [currentProject.id, cpId, csaId, action.id],
        onSuccess: (response) => new LoadCurrentCsaSuccess(response as any),
      }),
    ),
  );
}
